
import { useEffect, useState } from 'react';
import { Check, Plus, Shield, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  PainelPermissionsDialogShell,
  PainelRoleIcon,
} from '@/components/painel/permissions/painel-role-visuals';
import {
  painelButtonPrimaryClass,
  painelSelectContentClass,
} from '@/components/painel/painel-styles';
import { PainelRocketScroll } from '@/components/painel/PainelRocketScroll';
import { painelInputClass, painelSelectTriggerClass } from '@/components/painel/cms/painel-form';
import {
  ABSOLUTE_ADMIN_USERNAME,
  PAINEL_PERMISSION_LABELS,
  PAINEL_ROLE_DESCRIPTIONS,
  PAINEL_ROLE_LABELS,
  ROLE_PERMISSIONS,
  isAbsoluteAdminUsername,
  type PainelRole,
  type PainelUserStatus,
} from '@/lib/painel-rbac';
import type { PainelUserCreateFields } from '@/lib/painel-users-store';
import { PainelXatRankField } from '@/components/painel/PainelXatRankField';
import { PainelHelpCenterRoleField } from '@/components/painel/PainelHelpCenterRoleField';
import { xatRankOptionIdToOfficialLabel } from '@/lib/painel-user-xat-rank';
import { helpCenterRoleOptionToValue } from '@/lib/painel-user-help-center-role';
import type { StaffXatRankOptionId } from '@/lib/equipe-xat-ranks';
import type { SiteStaffRole } from '@/lib/cms-types';
import { cn } from '@/lib/utils';

const ROLE_ORDER: PainelRole[] = ['super_admin', 'admin', 'editor', 'reviewer'];

const CREATE_USER_VISUAL = {
  icon: UserPlus,
  iconShell: 'bg-gradient-to-br from-sky-500/25 to-sky-700/10 ring-sky-400/35',
  iconColor: 'text-sky-200',
  glow: 'shadow-[0_0_40px_-10px_rgba(56,189,248,0.35)]',
};

type PainelUserCreateModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (fields: PainelUserCreateFields) => boolean;
};

export function PainelUserCreateModal({
  open,
  onOpenChange,
  onCreate,
}: PainelUserCreateModalProps) {
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<PainelRole>('editor');
  const [status, setStatus] = useState<PainelUserStatus>('active');
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [xatRankOptionId, setXatRankOptionId] = useState<StaffXatRankOptionId | 'none'>('none');
  const [helpCenterRoleOptionId, setHelpCenterRoleOptionId] = useState<SiteStaffRole | 'none'>('none');

  useEffect(() => {
    if (!open) {
      setName('');
      setUsername('');
      setEmail('');
      setPassword('');
      setRole('editor');
      setStatus('active');
      setUsernameError(null);
      setXatRankOptionId('none');
      setHelpCenterRoleOptionId('none');
    }
  }, [open]);

  const rolePermissions = ROLE_PERMISSIONS[role] ?? [];
  const canSubmit =
    name.trim().length > 0 &&
    username.trim().length > 0 &&
    email.trim().length > 0 &&
    password.trim().length >= 4;

  const handleCreate = () => {
    if (!canSubmit) return;
    const nextUsername = username.trim().toLowerCase();
    if (isAbsoluteAdminUsername(nextUsername)) {
      setUsernameError(
        `O username "${ABSOLUTE_ADMIN_USERNAME}" é reservado à conta admin absoluta.`
      );
      return;
    }
    const ok = onCreate({
      name: name.trim(),
      username: nextUsername,
      email: email.trim(),
      password: password.trim(),
      role,
      status,
      officialXatRole: xatRankOptionIdToOfficialLabel(xatRankOptionId) ?? undefined,
      helpCenterRole: helpCenterRoleOptionToValue(helpCenterRoleOptionId) ?? undefined,
    });
    if (!ok) {
      setUsernameError('Este nome de usuário já está em uso.');
      return;
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="max-h-[90vh] max-w-2xl overflow-hidden border-0 bg-[#030023] p-0 shadow-none"
      >
        <DialogTitle className="sr-only">Adicionar usuário ao painel</DialogTitle>
        <PainelPermissionsDialogShell>
          <div className="border-b border-white/10 bg-blue-950/20 px-6 pb-5 pt-6 sm:px-8">
            <div className="flex items-start gap-4 pr-14">
              <PainelRoleIcon config={CREATE_USER_VISUAL} size="lg" />
              <div className="min-w-0 flex-1">
                <p className="font-inter text-[10px] font-bold uppercase tracking-[0.18em] text-sky-300/55">
                  Novo usuário
                </p>
                <h2 className="mt-1 font-poppins text-xl font-bold text-white sm:text-2xl">
                  Adicionar usuário
                </h2>
                <p className="mt-1.5 font-inter text-sm text-slate-400">
                  Crie um acesso ao painel com cargo e permissões — salvo localmente neste navegador.
                </p>
              </div>
            </div>
          </div>

          <PainelRocketScroll
            className='max-h-[min(56vh,540px)] bg-[#030023]/30'
            viewportClassName='px-6 py-5 sm:px-8 scroll-smooth'
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="create-user-name" className="text-slate-300">
                  Nome
                </Label>
                <input
                  id="create-user-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={painelInputClass}
                  placeholder="Ex: Guinho"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-user-username" className="text-slate-300">
                  Nome de usuário
                </Label>
                <input
                  id="create-user-username"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    setUsernameError(null);
                  }}
                  className={cn(painelInputClass, usernameError && 'border-rose-500/50')}
                  placeholder="Ex: guinho"
                  autoComplete="off"
                />
                {usernameError ? (
                  <p className="font-inter text-xs text-rose-300">{usernameError}</p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-user-email" className="text-slate-300">
                  E-mail
                </Label>
                <input
                  id="create-user-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={painelInputClass}
                  placeholder="Ex: guinho@xatajuda.com"
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="create-user-password" className="text-slate-300">
                  Senha
                </Label>
                <input
                  id="create-user-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={painelInputClass}
                  placeholder="Mínimo 4 caracteres"
                  autoComplete="new-password"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Cargo</Label>
                <Select value={role} onValueChange={(v) => setRole(v as PainelRole)}>
                  <SelectTrigger className={painelSelectTriggerClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={painelSelectContentClass}>
                    {ROLE_ORDER.map((r) => (
                      <SelectItem key={r} value={r} className="focus:bg-white/10 focus:text-white">
                        {PAINEL_ROLE_LABELS[r]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Status</Label>
                <Select
                  value={status}
                  onValueChange={(v) => setStatus(v as PainelUserStatus)}
                >
                  <SelectTrigger className={painelSelectTriggerClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={painelSelectContentClass}>
                    <SelectItem value="active" className="focus:bg-white/10 focus:text-white">
                      Ativo
                    </SelectItem>
                    <SelectItem value="inactive" className="focus:bg-white/10 focus:text-white">
                      Inativo
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <PainelXatRankField
                  value={xatRankOptionId}
                  onChange={setXatRankOptionId}
                />
              </div>
              <div className="sm:col-span-2">
                <PainelHelpCenterRoleField
                  value={helpCenterRoleOptionId}
                  onChange={setHelpCenterRoleOptionId}
                />
              </div>
            </div>

            <div className="mt-6 rounded-2xl border border-white/10 bg-blue-950/30 p-4 sm:p-5">
              <div className="mb-3 flex items-center gap-2">
                <Shield className="h-4 w-4 text-sky-400/70" aria-hidden />
                <h3 className="font-poppins text-xs font-bold uppercase tracking-[0.14em] text-slate-300">
                  Permissões do cargo {PAINEL_ROLE_LABELS[role]}
                </h3>
                <span className="ml-auto font-inter text-[11px] text-slate-500">
                  {rolePermissions.length} ativas
                </span>
              </div>
              <p className="mb-3 font-inter text-xs leading-relaxed text-slate-400">
                {PAINEL_ROLE_DESCRIPTIONS[role]}
              </p>
              <ul className="grid gap-1.5 sm:grid-cols-2">
                {rolePermissions.map((permission) => (
                  <li
                    key={permission}
                    className="flex items-center gap-2 font-inter text-[13px] text-blue-50"
                  >
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded border border-sky-400/50 bg-sky-500/25 text-sky-100">
                      <Check className="h-3 w-3" aria-hidden />
                    </span>
                    {PAINEL_PERMISSION_LABELS[permission]}
                  </li>
                ))}
              </ul>
            </div>
          </PainelRocketScroll>

          <div className="flex flex-col-reverse gap-2 border-t border-white/10 bg-blue-950/25 px-6 py-4 sm:flex-row sm:justify-end sm:px-8">
            <Button
              type="button"
              variant="ghost"
              className="h-10 rounded-xl text-slate-400 hover:bg-white/10 hover:text-white"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              disabled={!canSubmit}
              className={cn(painelButtonPrimaryClass, 'h-10 px-5')}
              onClick={handleCreate}
            >
              <Plus className="mr-2 h-4 w-4" />
              Adicionar usuário
            </Button>
          </div>
        </PainelPermissionsDialogShell>
      </DialogContent>
    </Dialog>
  );
}
